import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const LightAutomationSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  active: z.boolean(),
  order: z.number(),
  targetModel: z.enum(['invoice', 'debtor']),
  lastActivityAt: z.string().nullable(),
});

const AutomationsSchema = z.array(LightAutomationSchema);

export type Automations = z.infer<typeof AutomationsSchema>;
export type LightAutomation = z.infer<typeof LightAutomationSchema>;

export type AutomationsFilters = {
  page?: number;
  page_limit?: number;
  sort_order?: string;
};

export async function loadAutomationsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get('/automations', { params: queryKey[1] });

  const safeParse = AutomationsSchema.safeParse(data);

  if (!safeParse.success) {
    handleZodError(safeParse, 'automationIndex');
    throw safeParse.error;
  }

  return safeParse.data;
}

const emptyAutomations: Automations = [];

export function useLoadAutomations(params?: AutomationsFilters) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Automations, ApiError>({
    queryKey: ['automationIndex', params],
    queryFn: context => loadAutomationsQueryFn(axiosInstance, context),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyAutomations;
    },
  });

  return addResourceNameToQueryResult<Automations, AxiosError, 'automations', 'PreventUndefined'>(
    'automations',
    queryResult,
    emptyAutomations,
  );
}
